/**
 * Full-screen photo viewer with rotate and zoom — both VIEW-ONLY (owner's
 * decision, 2026-10-11): nothing here changes the stored photo, the AI reading
 * or the condition report.
 *
 * Zoom: the toolbar, the mouse wheel, double-click / double-tap, and a
 * two-finger pinch (handled here, so the page itself never zooms). Drag to pan
 * while zoomed. Keys: ← → photos, R / Shift+R rotate, + − zoom, 0 reset, Esc.
 * Each photo opens upright at 100%.
 *
 * Download saves the photo as stored (upright as taken, not as rotated here)
 * through `downloadUrl`, a link the API signs with Content-Disposition:
 * attachment — the photo is cross-origin, where `<a download>` is ignored.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import {
  IconChevronLeft,
  IconChevronRight,
  IconDownload,
  IconRotate,
  IconRotateClockwise,
  IconTrash,
  IconX,
  IconZoomIn,
  IconZoomOut,
} from '@tabler/icons-react';
import type { CheckInPhoto } from '@techgitter/contracts';

import { photoDownloadLink } from './photoDownload';
import {
  DOUBLE_TAP_SCALE,
  IDENTITY,
  MAX_SCALE,
  MIN_SCALE,
  ZOOM_STEP,
  isSideways,
  pan,
  rotate,
  toCss,
  zoomBy,
  zoomLabel,
  zoomTo,
  type ViewerTransform,
} from './photoViewerTransform';

export interface PhotoLightboxProps {
  photos: CheckInPhoto[];
  startIndex: number;
  onClose: () => void;
  /** Delete the photo on screen. The caller closes the lightbox and confirms. */
  onDelete?: (photo: CheckInPhoto) => void;
}

const ICON = { size: 22, stroke: 1.5 } as const;
const TOOL_SX = { color: 'white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' } };

export const PhotoLightbox: React.FC<PhotoLightboxProps> = ({ photos, startIndex, onClose, onDelete }) => {
  const [index, setIndex] = useState(startIndex);
  const [view, setView] = useState<ViewerTransform>(IDENTITY);
  const [gesturing, setGesturing] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  // Active pointers, for drag-to-pan and two-finger pinch.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ distance: number; view: ViewerTransform } | null>(null);

  const photo = photos[index];
  const canGoBack = index > 0;
  const canGoForward = index < photos.length - 1;

  // Every photo opens upright at 100%.
  useEffect(() => setView(IDENTITY), [index]);

  const go = useCallback(
    (delta: number) => setIndex(i => Math.min(photos.length - 1, Math.max(0, i + delta))),
    [photos.length],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'r' || e.key === 'R') setView(v => rotate(v, e.shiftKey ? -1 : 1));
      else if (e.key === '+' || e.key === '=') setView(v => zoomBy(v, ZOOM_STEP));
      else if (e.key === '-' || e.key === '_') setView(v => zoomBy(v, 1 / ZOOM_STEP));
      else if (e.key === '0') setView(v => ({ ...IDENTITY, rotation: v.rotation }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  // The wheel listener must be non-passive to stop the page scrolling, which
  // React's onWheel can't be.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setView(v => zoomBy(v, Math.exp(-e.deltaY * 0.002)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  if (!photo) return null;
  const src = photo.signedUrl || photo.blobUrl;
  const download = photoDownloadLink(photo);

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      pinch.current = { distance: Math.hypot(a.x - b.x, a.y - b.y), view };
    }
    setGesturing(true);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const last = pointers.current.get(e.pointerId);
    if (!last) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = Array.from(pointers.current.values());
      const start = pinch.current;
      setView(zoomTo(start.view, start.view.scale * (Math.hypot(a.x - b.x, a.y - b.y) / start.distance)));
    } else if (pointers.current.size === 1) {
      setView(v => pan(v, e.clientX - last.x, e.clientY - last.y));
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) setGesturing(false);
  };

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const sideways = isSideways(view);

  return (
    <Box
      onClick={onClose}
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'rgba(0, 0, 0, 0.92)',
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <Box
        ref={stageRef}
        onClick={stop}
        onDoubleClick={() => setView(v => (v.scale > MIN_SCALE ? zoomTo(v, MIN_SCALE) : zoomTo(v, DOUBLE_TAP_SCALE)))}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        sx={{
          touchAction: 'none',
          cursor: view.scale > MIN_SCALE ? (gesturing ? 'grabbing' : 'grab') : 'zoom-in',
          userSelect: 'none',
        }}
      >
        <img
          src={src}
          alt={photo.originalFilename ?? 'Inspection photo'}
          draggable={false}
          style={{
            display: 'block',
            // A quarter turn swaps which screen edge bounds the photo.
            maxWidth: sideways ? '82dvh' : '95vw',
            maxHeight: sideways ? '95vw' : '82dvh',
            objectFit: 'contain',
            transform: toCss(view),
            transition: gesturing ? 'none' : 'transform 0.15s ease',
          }}
        />
      </Box>

      <IconButton
        onClick={e => {
          e.stopPropagation();
          onClose();
        }}
        sx={{ position: 'absolute', top: 'calc(env(safe-area-inset-top) + 8px)', right: 8, color: 'white' }}
        aria-label="Close"
      >
        <IconX {...ICON} />
      </IconButton>
      {onDelete && (
        <IconButton
          onClick={e => {
            e.stopPropagation();
            onDelete(photo);
          }}
          sx={{ position: 'absolute', top: 'calc(env(safe-area-inset-top) + 8px)', right: 56, color: 'white' }}
          aria-label="Delete photo"
        >
          <IconTrash {...ICON} />
        </IconButton>
      )}
      {download && (
        <IconButton
          component="a"
          href={download.href}
          target={download.target}
          rel="noopener noreferrer"
          onClick={stop}
          sx={{ position: 'absolute', top: 'calc(env(safe-area-inset-top) + 8px)', right: onDelete ? 104 : 56, color: 'white' }}
          aria-label="Download photo"
          title="Download"
        >
          <IconDownload {...ICON} />
        </IconButton>
      )}
      {canGoBack && (
        <IconButton
          onClick={e => {
            e.stopPropagation();
            go(-1);
          }}
          sx={{ position: 'absolute', left: 8, color: 'white' }}
          aria-label="Previous"
        >
          <IconChevronLeft size={28} stroke={1.5} />
        </IconButton>
      )}
      {canGoForward && (
        <IconButton
          onClick={e => {
            e.stopPropagation();
            go(1);
          }}
          sx={{ position: 'absolute', right: 8, color: 'white' }}
          aria-label="Next"
        >
          <IconChevronRight size={28} stroke={1.5} />
        </IconButton>
      )}

      <Box
        onClick={stop}
        role="toolbar"
        aria-label="Photo view"
        sx={{
          position: 'absolute',
          bottom: 'calc(env(safe-area-inset-bottom) + 16px)',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          px: '6px',
          py: '4px',
          borderRadius: '10px',
          bgcolor: 'rgba(255, 255, 255, 0.12)',
          backdropFilter: 'blur(6px)',
        }}
      >
        <IconButton sx={TOOL_SX} onClick={() => setView(v => rotate(v, -1))} aria-label="Rotate left" title="Rotate left (Shift+R)">
          <IconRotate {...ICON} />
        </IconButton>
        <IconButton sx={TOOL_SX} onClick={() => setView(v => rotate(v, 1))} aria-label="Rotate right" title="Rotate right (R)">
          <IconRotateClockwise {...ICON} />
        </IconButton>
        <Box sx={{ width: '0.5px', alignSelf: 'stretch', my: '6px', mx: '4px', bgcolor: 'rgba(255,255,255,0.3)' }} />
        <IconButton
          sx={TOOL_SX}
          onClick={() => setView(v => zoomBy(v, 1 / ZOOM_STEP))}
          disabled={view.scale <= MIN_SCALE}
          aria-label="Zoom out"
          title="Zoom out (−)"
        >
          <IconZoomOut {...ICON} />
        </IconButton>
        <Box
          component="button"
          type="button"
          onClick={() => setView(v => zoomTo(v, MIN_SCALE))}
          title="Reset zoom (0)"
          aria-label={`Zoom ${zoomLabel(view)} — reset`}
          sx={{
            minWidth: 52,
            height: 32,
            border: 'none',
            borderRadius: '6px',
            bgcolor: 'transparent',
            color: 'white',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
          }}
        >
          {zoomLabel(view)}
        </Box>
        <IconButton
          sx={TOOL_SX}
          onClick={() => setView(v => zoomBy(v, ZOOM_STEP))}
          disabled={view.scale >= MAX_SCALE}
          aria-label="Zoom in"
          title="Zoom in (+)"
        >
          <IconZoomIn {...ICON} />
        </IconButton>
      </Box>
    </Box>
  );
};
